import React, { useState, useEffect } from 'react';
import { Icon, Button, Card, CardHeader, CardBody, Badge, Dialog, DialogHeader, DialogBody, DialogFooter, Input } from '@repo/ui';
import type { ConfigModalProps, TenantSettingsModuleConfig } from '@xtnd/web/shared/types/tenantSettingTypes';
import { showToast } from '@repo/ui';
import { emailVerificationApi } from '@xtnd/web/features/settings/api/emailVerificationApi';

interface EmailEntry {
  email: string;
  verificationStatus?: 'not_started' | 'pending_verification' | 'verified' | 'failed';
  lastChecked?: Date;
}

interface TenantSettingsFormData {
  // Core tenant info (root level)
  tenantName: string;
  alias: string;
  // Unified authorized domains (replaces primaryDomain + domains)
  authorizedDomains: string[];
  // Individual email overrides for tenant access
  allowedEmails: string[];
  // Default from email with verification
  defaultFromEmail: EmailEntry;
  contactInfo: {
    admin: EmailEntry[];
    technicalSupport: EmailEntry[];
    operationalSupport: EmailEntry[];
  };
}

interface TenantSettingsModalProps extends ConfigModalProps {
  initialData?: TenantSettingsModuleConfig & {
    tenantName?: string;
    alias?: string;
    authorizedDomains?: string[];
    allowedEmails?: string[];
    updatedAt?: string;
  };
  onSave: (data: TenantSettingsFormData) => Promise<void>;
}

// Email validation regex
const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Domain validation regex (2+ parts)
const domainRegex = /^([a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}$/;

export const TenantSettingsModal: React.FC<TenantSettingsModalProps> = ({
  isOpen,
  onClose,
  onSave,
  initialData,
  isLoading = false,
}) => {
  const [formData, setFormData] = useState<TenantSettingsFormData>({
    // Core tenant info
    tenantName: '',
    alias: '',
    // Unified authorized domains
    authorizedDomains: [],
    // Individual email overrides
    allowedEmails: [],
    // Default from email with verification status
    defaultFromEmail: {
      email: '',
      verificationStatus: 'not_started',
      lastChecked: undefined
    },
    contactInfo: {
      admin: [],
      technicalSupport: [],
      operationalSupport: [],
    },
  });

  const [newDomain, setNewDomain] = useState('');
  const [newAllowedEmail, setNewAllowedEmail] = useState('');
  const [emailErrors, setEmailErrors] = useState<Record<string, string>>({});
  const [domainErrors, setDomainErrors] = useState<Record<string, string>>({});

  // Email verification mutations for default from email
  const [verifyingEmail, setVerifyingEmail] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(false);

  // Verification functions for default from email
  const handleVerifyDefaultFromEmail = async () => {
    if (!formData.defaultFromEmail?.email?.trim()) {
      showToast.error('Please enter an email address first');
      return;
    }

    if (!emailRegex.test(formData.defaultFromEmail.email)) {
      showToast.error('Please enter a valid email address');
      return;
    }

    try {
      setVerifyingEmail(true);

      const result = await emailVerificationApi.verifyEmail(formData.defaultFromEmail?.email || '');

      setFormData(prev => ({
        ...prev,
        defaultFromEmail: {
          ...prev.defaultFromEmail,
          verificationStatus: 'pending_verification' as const,
          lastChecked: new Date(),
        },
      }));

      showToast.success(result.message || 'Verification email sent successfully');

    } catch (error: any) {
      console.error('Failed to verify email:', error);
      showToast.error(error.message || 'Failed to send verification email');
    } finally {
      setVerifyingEmail(false);
    }
  };

  const handleCheckVerificationStatus = async () => {
    if (!formData.defaultFromEmail?.email?.trim()) {
      showToast.error('Please enter an email address first');
      return;
    }

    try {
      setCheckingStatus(true);

      console.log('Checking verification status for:', formData.defaultFromEmail?.email);
      const result = await emailVerificationApi.checkVerificationStatus(formData.defaultFromEmail?.email || '');
      console.log('API response:', result);

      const newStatus: 'not_started' | 'pending_verification' | 'verified' | 'failed' =
                       result.data?.status === 'Success' ? 'verified' :
                       result.data?.status === 'Pending' ? 'pending_verification' :
                       result.data?.status === 'Failed' ? 'failed' : 'not_started';

      console.log('Mapped status:', newStatus);

      setFormData(prev => {
        console.log('Previous formData:', prev);
        const newFormData = {
          ...prev,
          defaultFromEmail: {
            ...prev.defaultFromEmail,
            verificationStatus: newStatus,
            lastChecked: new Date(),
          },
        };
        console.log('New formData:', newFormData);
        return newFormData;
      });

      if (newStatus === 'verified') {
        showToast.success('Email is verified and ready to use!');
      } else if (newStatus === 'pending_verification') {
        showToast.info('Email verification is still pending');
      } else {
        showToast.warning('Email verification failed or not started');
      }

    } catch (error: any) {
      console.error('Failed to check verification status:', error);
      showToast.error(error.message || 'Failed to check verification status');
    } finally {
      setCheckingStatus(false);
    }
  };

  // Initialize form data when modal opens or initialData changes
  useEffect(() => {
    if (initialData) {
      // Convert legacy email structure to new array structure
      const existingContactInfo = initialData.contactInfo || {};
      const existingEmailNotifications = initialData.emailNotifications || {};

      // Handle both new authorizedDomains and legacy primaryDomain + domains
      let authorizedDomains = initialData.authorizedDomains || [];
      if (authorizedDomains.length === 0) {
        // Convert from legacy structure
        const primaryDomain = (initialData as any).primaryDomain;
        const additionalDomains = initialData.domains || [];
        authorizedDomains = [
          ...(primaryDomain ? [primaryDomain] : []),
          ...additionalDomains,
        ].filter(Boolean); // Remove empty values
      }

      // Ensure at least one domain exists
      if (authorizedDomains.length === 0) {
        authorizedDomains = ['example.com']; // Default placeholder
      }

      setFormData({
        // Core tenant info (passed from parent)
        tenantName: initialData.tenantName || '',
        alias: initialData.alias || '',
        // Unified authorized domains
        authorizedDomains,
        // Individual email overrides
        allowedEmails: initialData.allowedEmails || [],
        // Default from email with verification status (ensure object always exists)
        defaultFromEmail: initialData.emailServiceConfig?.defaultFromEmail ? {
          email: initialData.emailServiceConfig.defaultFromEmail,
          verificationStatus: initialData.emailServiceConfig.defaultFromEmailVerified ? 'verified' : 'not_started',
          lastChecked: undefined
        } : {
          email: '',
          verificationStatus: 'not_started' as const,
          lastChecked: undefined
        },
        contactInfo: {
          admin: existingContactInfo.admin ||
                 (existingContactInfo.adminEmail?.address ? [{ email: existingContactInfo.adminEmail.address, verificationStatus: 'verified' }] : []),
          technicalSupport: existingContactInfo.technicalSupport ||
                           (existingEmailNotifications.technicalSupportEmail ? [{ email: existingEmailNotifications.technicalSupportEmail, verificationStatus: 'verified' }] : []),
          operationalSupport: existingContactInfo.operationalSupport ||
                             (existingEmailNotifications.operationsSupportEmail ? [{ email: existingEmailNotifications.operationsSupportEmail, verificationStatus: 'verified' }] : []),
        },
      });
    }
  }, [initialData, isOpen]);

  // Validate email format
  const validateEmail = (email: string, fieldName: string) => {
    if (email && !emailRegex.test(email)) {
      setEmailErrors(prev => ({ ...prev, [fieldName]: 'Please enter a valid email address' }));
      return false;
    } else {
      setEmailErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[fieldName];
        return newErrors;
      });
      return true;
    }
  };

  // Validate domain format
  const validateDomain = (domain: string, fieldName: string) => {
    if (domain && !domainRegex.test(domain)) {
      setDomainErrors(prev => ({ ...prev, [fieldName]: 'Please enter a valid domain (e.g., example.com)' }));
      return false;
    } else {
      setDomainErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[fieldName];
        return newErrors;
      });
      return true;
    }
  };

  const handleAddDomain = () => {
    if (newDomain.trim() && !formData.authorizedDomains.includes(newDomain.trim())) {
      if (validateDomain(newDomain.trim(), 'newDomain')) {
        setFormData(prev => ({
          ...prev,
          authorizedDomains: [...prev.authorizedDomains, newDomain.trim()],
        }));
        setNewDomain('');
      }
    }
  };

  const handleRemoveDomain = (index: number) => {
    // Ensure at least one domain remains
    if (formData.authorizedDomains.length <= 1) {
      showToast.error('You must have at least one authorized domain');
      return;
    }

    setFormData(prev => ({
      ...prev,
      authorizedDomains: prev.authorizedDomains.filter((_, i) => i !== index),
    }));
  };

  const handleAddAllowedEmail = () => {
    const trimmed = newAllowedEmail.trim().toLowerCase();
    if (trimmed && !formData.allowedEmails.includes(trimmed)) {
      if (emailRegex.test(trimmed)) {
        setFormData(prev => ({
          ...prev,
          allowedEmails: [...prev.allowedEmails, trimmed],
        }));
        setNewAllowedEmail('');
        setEmailErrors(prev => {
          const next = { ...prev };
          delete next['newAllowedEmail'];
          return next;
        });
      } else {
        setEmailErrors(prev => ({ ...prev, newAllowedEmail: 'Please enter a valid email address' }));
      }
    }
  };

  const handleRemoveAllowedEmail = (index: number) => {
    setFormData(prev => ({
      ...prev,
      allowedEmails: prev.allowedEmails.filter((_, i) => i !== index),
    }));
  };

  const handleCoreInfoChange = (field: 'tenantName' | 'alias', value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  // Email array management functions
  const handleAddEmail = (category: keyof TenantSettingsFormData['contactInfo']) => {
    setFormData(prev => ({
      ...prev,
      contactInfo: {
        ...prev.contactInfo,
        [category]: [...prev.contactInfo[category], { email: '', verificationStatus: 'verified' }], // No verification required for notification emails
      },
    }));
  };

  const handleRemoveEmail = (category: keyof TenantSettingsFormData['contactInfo'], index: number) => {
    setFormData(prev => ({
      ...prev,
      contactInfo: {
        ...prev.contactInfo,
        [category]: prev.contactInfo[category].filter((_, i) => i !== index),
      },
    }));
  };

  const handleEmailChange = (
    category: keyof TenantSettingsFormData['contactInfo'],
    index: number,
    value: string
  ) => {
    setFormData(prev => ({
      ...prev,
      contactInfo: {
        ...prev.contactInfo,
        [category]: prev.contactInfo[category].map((item, i) =>
          i === index ? { email: value } : item
        ),
      },
    }));
    validateEmail(value, `${category}[${index}]`);
  };

  const handleSave = async () => {
    // Validate all email fields before saving
    let allEmailsValid = true;

    // Validate default from email
    if (formData.defaultFromEmail?.email && !validateEmail(formData.defaultFromEmail.email, 'defaultFromEmail')) {
      allEmailsValid = false;
    }

    // Validate all emails in arrays
    formData.contactInfo.admin.forEach((item, index) => {
      if (!validateEmail(item.email, `admin[${index}]`)) {
        allEmailsValid = false;
      }
    });

    formData.contactInfo.technicalSupport.forEach((item, index) => {
      if (!validateEmail(item.email, `technicalSupport[${index}]`)) {
        allEmailsValid = false;
      }
    });

    formData.contactInfo.operationalSupport.forEach((item, index) => {
      if (!validateEmail(item.email, `operationalSupport[${index}]`)) {
        allEmailsValid = false;
      }
    });

    // Validate authorized domains
    let allDomainsValid = true;
    if (formData.authorizedDomains.length === 0) {
      showToast.error('You must have at least one authorized domain');
      return;
    }

    formData.authorizedDomains.forEach((domain, index) => {
      if (!validateDomain(domain, `authorizedDomain[${index}]`)) {
        allDomainsValid = false;
      }
    });

    if (!allEmailsValid || !allDomainsValid) {
      return;
    }

    if (!formData.tenantName.trim() || !formData.alias.trim()) {
      return;
    }

    // Check if default from email is provided but not verified
    if (formData.defaultFromEmail?.email && formData.defaultFromEmail.verificationStatus !== 'verified') {
      showToast.error('Please verify your default from email address before saving');
      return;
    }

    try {
      await onSave(formData);
      onClose();
    } catch (error) {
      console.error('Failed to save tenant settings:', error);
    }
  };

  // Format timestamp for display
  const formatTimestamp = (timestamp?: string) => {
    if (!timestamp) return 'Never updated';
    return new Date(timestamp).toLocaleString();
  };

  // Verification icon and button functions removed - not needed for notification emails

  const renderEmailArraySection = (
    title: string,
    category: keyof TenantSettingsFormData['contactInfo'],
    placeholder: string
  ) => (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h4 className="text-md font-medium text-xtnd-dark dark:text-xtnd-white">
          {title}
        </h4>
        <Button
          onClick={() => handleAddEmail(category)}
          variant="outline"
          size="md"
          className="flex items-center space-x-1"
        >
          <Icon name="HiPlus" className="h-4 w-4" />
          <span>Add</span>
        </Button>
      </div>

      {formData.contactInfo[category].length === 0 && (
        <p className="text-sm text-xtnd-dark-400 dark:text-xtnd-dark-300 italic">
          No {title.toLowerCase()} configured. Click &quot;Add&quot; to add one.
        </p>
      )}

      {formData.contactInfo[category].map((item, index) => (
        <div key={index} className="space-y-2">
          <div className="flex space-x-2 items-start">
            <div className="flex-1">
              <Input
                type="email"
                leftIcon={<Icon name="HiMail" className="h-4 w-4" />}
                value={item.email}
                onChange={(e) => handleEmailChange(category, index, e.target.value)}
                placeholder={placeholder}
                fullWidth
                error={emailErrors[`${category}[${index}]`]}
              />
            </div>

              {/* Remove verification button; keep delete */}
              <div className="flex items-center space-x-2">
                <Button
                  onClick={() => handleRemoveEmail(category, index)}
                  variant="ghost"
                  size="md"
                  className="text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                >
                  <Icon name="HiTrash" className="h-4 w-4" />
                </Button>
              </div>
          </div>

          {/* Remove verification status indicators for tenant settings emails */}
        </div>
      ))}
    </div>
  );

  return (
    <Dialog open={isOpen} onClose={onClose} size="lg">
      <DialogHeader>
        <div className="flex items-center justify-between w-full pr-8">
          <h2 className="text-xl font-semibold text-xtnd-dark dark:text-xtnd-white">
            Tenant Settings
          </h2>
          {initialData?.updatedAt && (
            <div className="flex items-center text-xs text-xtnd-dark-400 dark:text-xtnd-dark-300 whitespace-nowrap">
              <Icon name="HiClock" className="h-3 w-3 mr-1" />
              Updated: {formatTimestamp(initialData.updatedAt)}
            </div>
          )}
        </div>
      </DialogHeader>

      <DialogBody className="max-h-[70vh] overflow-y-auto">
        <div className="space-y-6">
          {/* Core Tenant Information */}
          <Card variant="default">
            <CardHeader>
              <div className="flex items-center space-x-2">
                <Icon name="HiUser" className="h-5 w-5 text-purple-600" />
                <h3 className="text-lg font-semibold text-xtnd-dark dark:text-xtnd-white">
                  Tenant Information
                </h3>
              </div>
            </CardHeader>
            <CardBody>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Tenant Name"
                  leftIcon={<Icon name="HiOfficeBuilding" className="h-4 w-4" />}
                  value={formData.tenantName}
                  onChange={(e) => handleCoreInfoChange('tenantName', e.target.value)}
                  placeholder="The Estate Lawyers"
                  fullWidth
                  required
                />
                <Input
                  label="Alias"
                  leftIcon={<Icon name="HiUser" className="h-4 w-4" />}
                  value={formData.alias}
                  onChange={(e) => handleCoreInfoChange('alias', e.target.value)}
                  placeholder="TEL"
                  fullWidth
                  required
                />
              </div>
            </CardBody>
          </Card>

          {/* Authorized Domains Section */}
          <Card variant="default">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Icon name="HiGlobe" className="h-5 w-5 text-xtnd-blue" />
                  <h3 className="text-lg font-semibold text-xtnd-dark dark:text-xtnd-white">
                    Authorized Domains
                  </h3>
                </div>
                <Badge variant="primary" size="sm">
                  {formData.authorizedDomains.length} domain{formData.authorizedDomains.length !== 1 ? 's' : ''}
                </Badge>
              </div>
              <p className="text-sm text-xtnd-dark-500 dark:text-xtnd-dark-300 mt-2">
                Pre-token generation will validate against these domains. At least one domain is required.
              </p>
            </CardHeader>
            <CardBody>
              <div className="space-y-3">
                {/* Add Domain */}
                <div className="flex space-x-2">
                  <div className="flex-1">
                    <Input
                      value={newDomain}
                      onChange={(e) => setNewDomain(e.target.value)}
                      placeholder="Enter domain (e.g., example.com)"
                      onKeyPress={(e) => e.key === 'Enter' && handleAddDomain()}
                      fullWidth
                      error={domainErrors['newDomain']}
                    />
                  </div>
                  <Button
                    onClick={handleAddDomain}
                    variant="primary"
                    size="md"
                    disabled={!newDomain.trim()}
                  >
                    <Icon name="HiPlus" className="h-4 w-4" />
                  </Button>
                </div>

                {/* Domain List - styled like input fields */}
                {formData.authorizedDomains.length > 0 && (
                  <div className="space-y-2">
                    {formData.authorizedDomains.map((domain, index) => (
                      <div key={index} className="flex space-x-2">
                        <div className="flex items-center flex-1 px-4 py-2 bg-white dark:bg-xtnd-dark-800 border border-xtnd-dark-200 dark:border-xtnd-dark-500 rounded-lg text-sm text-xtnd-dark-800 dark:text-xtnd-dark-50">
                          <span className="flex-1">
                            {domain}
                          </span>
                          {index === 0 && formData.authorizedDomains.length === 1 && (
                            <Badge variant="secondary" size="sm" className="text-xs">
                              Required
                            </Badge>
                          )}
                        </div>
                        <Button
                          onClick={() => handleRemoveDomain(index)}
                          variant="ghost"
                          size="sm"
                          className="text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                          disabled={formData.authorizedDomains.length === 1}
                          title={formData.authorizedDomains.length === 1 ? 'At least one domain is required' : 'Remove domain'}
                        >
                          <Icon name="HiTrash" className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </CardBody>
          </Card>

          {/* Allowed Emails (Individual Overrides) */}
          <Card variant="default">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Icon name="HiMail" className="h-5 w-5 text-xtnd-blue" />
                  <h3 className="text-lg font-semibold text-xtnd-dark dark:text-xtnd-white">
                    Allowed Emails
                  </h3>
                </div>
                {formData.allowedEmails.length > 0 && (
                  <Badge variant="primary" size="sm">
                    {formData.allowedEmails.length} email{formData.allowedEmails.length !== 1 ? 's' : ''}
                  </Badge>
                )}
              </div>
              <p className="text-sm text-xtnd-dark-500 dark:text-xtnd-dark-300 mt-2">
                Grant individual email addresses access to this tenant, even if their domain is not in the authorized domains list above.
              </p>
            </CardHeader>
            <CardBody>
              <div className="space-y-3">
                {/* Add Email */}
                <div className="flex space-x-2">
                  <div className="flex-1">
                    <Input
                      type="email"
                      value={newAllowedEmail}
                      onChange={(e) => {
                        setNewAllowedEmail(e.target.value);
                        if (emailErrors['newAllowedEmail']) {
                          setEmailErrors(prev => {
                            const next = { ...prev };
                            delete next['newAllowedEmail'];
                            return next;
                          });
                        }
                      }}
                      placeholder="Enter email (e.g., user@gmail.com)"
                      onKeyPress={(e) => e.key === 'Enter' && handleAddAllowedEmail()}
                      fullWidth
                      error={emailErrors['newAllowedEmail']}
                    />
                  </div>
                  <Button
                    onClick={handleAddAllowedEmail}
                    variant="primary"
                    size="md"
                    disabled={!newAllowedEmail.trim()}
                  >
                    <Icon name="HiPlus" className="h-4 w-4" />
                  </Button>
                </div>

                {/* Email List */}
                {formData.allowedEmails.length > 0 && (
                  <div className="space-y-2">
                    {formData.allowedEmails.map((email, index) => (
                      <div key={index} className="flex space-x-2">
                        <div className="flex items-center flex-1 px-4 py-2 bg-white dark:bg-xtnd-dark-800 border border-xtnd-dark-200 dark:border-xtnd-dark-500 rounded-lg text-sm text-xtnd-dark-800 dark:text-xtnd-dark-50">
                          <span className="flex-1">{email}</span>
                        </div>
                        <Button
                          onClick={() => handleRemoveAllowedEmail(index)}
                          variant="ghost"
                          size="sm"
                          className="text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                          title="Remove email"
                        >
                          <Icon name="HiTrash" className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}

                {formData.allowedEmails.length === 0 && (
                  <p className="text-sm text-xtnd-dark-400 dark:text-xtnd-dark-300 italic">
                    No individual email overrides configured.
                  </p>
                )}
              </div>
            </CardBody>
          </Card>

          {/* Default From Email Section */}
          <Card variant="default">
            <CardHeader>
              <div className="flex items-center space-x-2">
                <Icon name="HiMail" className="h-5 w-5 text-xtnd-blue" />
                <h3 className="text-lg font-semibold text-xtnd-dark dark:text-xtnd-white">
                  Default From Email
                </h3>
              </div>
              <p className="text-sm text-xtnd-dark-500 dark:text-xtnd-dark-300 mt-2">
                This email address will be used as the default sender for all transactional emails sent from Juniper.
              </p>
            </CardHeader>
            <CardBody>
              <div className="space-y-4">
                <div className="flex flex-col space-x-2 items-start">
                <Input
                      type="email"
                      label="Default From Email Address"
                      leftIcon={<Icon name="HiMail" className="h-4 w-4" />}
                      value={formData.defaultFromEmail?.email || ''}
                      onChange={(e) => {
                        setFormData(prev => ({
                          ...prev,
                          defaultFromEmail: {
                            ...prev.defaultFromEmail,
                            email: e.target.value,
                            verificationStatus: 'not_started', // Reset status when email changes
                          },
                        }));
                        validateEmail(e.target.value, 'defaultFromEmail');
                      }}
                      placeholder="noreply@yourdomain.com"
                      fullWidth
                      error={emailErrors['defaultFromEmail']}
                    />

                  <div className="flex items-center space-x-2 mt-6">
                    {/* Verification Status Icon */}
                    {formData.defaultFromEmail.verificationStatus === 'verified' && (
                      <div className="flex items-center space-x-1 text-green-600">
                        <Icon name="HiCheck" className="h-5 w-5" />
                        <span className="text-sm font-medium">Verified</span>
                      </div>
                    )}

                    {formData.defaultFromEmail.verificationStatus === 'pending_verification' && (
                      <div className="flex items-center space-x-1 text-yellow-600">
                        <Icon name="HiExclamationCircle" className="h-5 w-5" />
                        <span className="text-sm font-medium">Pending</span>
                      </div>
                    )}

                    {formData.defaultFromEmail.verificationStatus === 'failed' && (
                      <div className="flex items-center space-x-1 text-red-600">
                        <Icon name="HiExclamationCircle" className="h-5 w-5" />
                        <span className="text-sm font-medium">Failed</span>
                      </div>
                    )}

                    {/* Verify Button */}
                    <Button
                      onClick={handleVerifyDefaultFromEmail}
                      variant="outline"
                      size="md"
                      loading={verifyingEmail}
                      disabled={!formData.defaultFromEmail?.email?.trim() || !emailRegex.test(formData.defaultFromEmail?.email || '')}
                      className="whitespace-nowrap"
                    >
                      {verifyingEmail ? 'Sending...' : 'Verify Email'}
                    </Button>

                    {/* Check Status Button */}
                    <Button
                      onClick={handleCheckVerificationStatus}
                      variant="outline"
                      size="md"
                      loading={checkingStatus}
                      disabled={!formData.defaultFromEmail?.email?.trim()}
                      className="whitespace-nowrap"
                    >
                      {checkingStatus ? 'Checking...' : 'Check Status'}
                    </Button>
                  </div>
                </div>

                {/* Help Text */}
                <div className="text-sm text-xtnd-dark-500 dark:text-xtnd-dark-300">
                  <p>
                    <strong>How it works:</strong> Enter your desired from email address and click "Verify Email".
                    We'll send a verification email to that address. Once you click the verification link in the email,
                    the status will show as "Verified" and you can use this email for sending transactional emails.
                  </p>
                </div>
              </div>
            </CardBody>
          </Card>

          {/* Contact Information - Arrays */}
          <Card variant="default">
            <CardHeader>
              <div className="flex items-center space-x-2">
                  <Icon name="HiMail" className="h-5 w-5 text-green-600" />
                  <h3 className="text-lg font-semibold text-xtnd-dark dark:text-xtnd-white">
                  Contact Information
                </h3>
              </div>
            </CardHeader>
            <CardBody>
              <div className="space-y-6">
                {renderEmailArraySection('Admin Emails', 'admin', 'admin@company.com')}
                {renderEmailArraySection('Technical Support Emails', 'technicalSupport', 'techsupport@company.com')}
                {renderEmailArraySection('Operations Support Emails', 'operationalSupport', 'support@company.com')}
              </div>
            </CardBody>
          </Card>


        </div>
      </DialogBody>

      <DialogFooter>
        <Button
          onClick={onClose}
          variant="outline"
          disabled={isLoading}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="primary"
          loading={isLoading}
          disabled={
            !formData.tenantName.trim() ||
            !formData.alias.trim() ||
            formData.authorizedDomains.length === 0 ||
            Object.keys(emailErrors).length > 0 ||
            Object.keys(domainErrors).length > 0
          }
        >
          Save Changes
        </Button>
      </DialogFooter>
    </Dialog>
  );
};
